import React from "react";
import {Button, Card, CardBody, CardHeader, Col, Container, Input, Row, Table} from "reactstrap";
import {inject, observer} from "mobx-react";
import {Link} from "react-router-dom";
import {Bar} from "react-chartjs-2";
import moment from "moment";
import getRoutes from "../../../components/Akitatek/getRoutes";
import {delaiLisible, dureeLisible, grilleCreneaux} from "../../../util/telephonieFormat";

const JOURS = ["", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const PERIODES = [["7 jours", 7], ["30 jours", 30], ["3 mois", 91], ["12 mois", 365]];

/**
 * Statistiques des appels reçus sur la ligne du magasin : volumes, taux de réponse, créneaux chargés,
 * rappels des appels manqués et clients qui appellent le plus.
 */
const StatistiquesAppels = (inject("rootStore")(observer(class StatistiquesAppels extends React.Component {

    get store() {
        return this.props.rootStore.telephonieStore;
    }

    constructor(props, context) {
        super(props, context);
        this.props.rootStore.savedDatasStore.setHeader("Statistiques des appels", "Appels reçus sur la ligne du magasin", "default.jpg");
    }

    componentDidMount() {
        this.store.getStatistiques();
    }

    tuile(titre, valeur, detail, couleur = "dark") {
        return (
            <Col xs={6} md={3} className="mb-3" key={titre}>
                <Card className="shadow-sm h-100">
                    <CardBody className="py-3">
                        <div className="text-muted small">{titre}</div>
                        <div className={"h3 mb-0 text-" + couleur}>{valeur}</div>
                        {detail && <div className="small text-muted">{detail}</div>}
                    </CardBody>
                </Card>
            </Col>
        );
    }

    graphique(stats) {
        const parSemaine = stats.regroupement === "semaine";
        const labels = stats.evolution.map(l => parSemaine
            ? "Sem. du " + moment(l.periode).format("DD/MM")
            : moment(l.periode).format("ddd DD/MM"));

        return (
            <article style={{height: "20em", width: "100%"}}>
                <Bar
                    data={{
                        labels,
                        datasets: [
                            {label: "Répondus", data: stats.evolution.map(l => l.repondus), backgroundColor: "rgba(45, 206, 137, 0.7)"},
                            {label: "Manqués", data: stats.evolution.map(l => l.manques), backgroundColor: "rgba(245, 54, 92, 0.7)"},
                        ],
                    }}
                    options={{
                        maintainAspectRatio: false,
                        legend: {display: true, position: "top"},
                        scales: {
                            xAxes: [{stacked: true}],
                            yAxes: [{stacked: true, ticks: {beginAtZero: true, precision: 0}}],
                        },
                    }}
                />
            </article>
        );
    }

    creneaux(stats) {
        const grille = grilleCreneaux(stats.creneaux);

        return (
            <div style={{overflowX: "auto"}}>
                <Table size="sm" bordered className="text-center mb-1" style={{fontSize: "0.8rem"}}>
                    <thead>
                    <tr>
                        <th/>
                        {grille.heures.map(h => <th key={h}>{h} h</th>)}
                    </tr>
                    </thead>
                    <tbody>
                    {grille.lignes.map(ligne => (
                        <tr key={ligne.jour}>
                            <th className="text-left">{JOURS[ligne.jour]}</th>
                            {ligne.cases.map(c => (
                                <td key={c.heure}
                                    title={`${JOURS[c.jour]} ${c.heure} h : ${c.recus} appel(s), ${c.manques} manqué(s)`}
                                    style={{backgroundColor: c.recus ? `rgba(17, 205, 239, ${0.15 + 0.85 * c.recus / grille.max})` : undefined}}>
                                    {c.recus || ""}
                                    {c.manques > 0 && <div className="text-danger" style={{fontWeight: 600}}>{c.manques} m</div>}
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </Table>
                <small className="text-muted">Nombre d'appels reçus par créneau ; en rouge, les appels manqués (« m »).</small>
            </div>
        );
    }

    render() {
        const store = this.store;
        const stats = store.statistiques;

        return (
            <Container className="mt-2" fluid>
                <Card className="shadow mb-3">
                    <CardHeader style={{display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "center"}}>
                        <h4 className="mb-0">
                            Statistiques des appels{" "}
                            <Link to={getRoutes("Appels")} className="btn btn-sm btn-outline-secondary ml-2">Journal des appels</Link>
                        </h4>
                        <div style={{display: "flex", flexWrap: "wrap", alignItems: "center"}}>
                            {PERIODES.map(([libelle, jours]) => (
                                <Button key={jours} size="sm" color="secondary" className="mr-2 mb-1" onClick={() => store.setPeriode(jours)}>
                                    {libelle}
                                </Button>
                            ))}
                            <Input type="date" bsSize="sm" style={{width: 150}} className="mr-1 mb-1" value={store.statsDu}
                                   onChange={e => store.statsDu = e.target.value}/>
                            <span className="mr-1 mb-1">au</span>
                            <Input type="date" bsSize="sm" style={{width: 150}} className="mr-2 mb-1" value={store.statsAu}
                                   onChange={e => store.statsAu = e.target.value}/>
                            <Button size="sm" color="primary" className="mb-1" onClick={() => store.getStatistiques()}>Afficher</Button>
                        </div>
                    </CardHeader>
                </Card>

                {stats && <>
                    <p className="text-muted">
                        Du {moment(stats.du).format("DD/MM/YYYY")} au {moment(stats.au).format("DD/MM/YYYY")}.
                        Les chiffres commencent à la mise en service de la téléphonie dans le CRM et ne comptent plus les appels manqués supprimés.
                    </p>

                    <Row>
                        {this.tuile("Appels reçus", stats.totaux.recus,
                            `${stats.totaux.clients_connus} de clients connus, ${stats.totaux.numeros_inconnus} inconnus`)}
                        {this.tuile("Répondus", stats.totaux.repondus,
                            stats.totaux.taux_reponse !== null ? `${stats.totaux.taux_reponse} % de réponse` : null, "success")}
                        {this.tuile("Manqués", stats.totaux.manques,
                            `${stats.rappels.manques_a_traiter} encore à traiter`, "danger")}
                        {this.tuile("Durée des conversations", dureeLisible(stats.totaux.duree_moyenne),
                            `en moyenne, ${dureeLisible(stats.totaux.duree_totale)} au total`)}
                    </Row>

                    <Card className="shadow mb-3">
                        <CardHeader><h5 className="mb-0">Appels par {stats.regroupement}</h5></CardHeader>
                        <CardBody>{this.graphique(stats)}</CardBody>
                    </Card>

                    <Card className="shadow mb-3">
                        <CardHeader><h5 className="mb-0">Créneaux chargés</h5></CardHeader>
                        <CardBody>{this.creneaux(stats)}</CardBody>
                    </Card>

                    <Row>
                        <Col lg={6}>
                            <Card className="shadow mb-3">
                                <CardHeader><h5 className="mb-0">Rappel des appels manqués</h5></CardHeader>
                                <CardBody>
                                    <p>
                                        {stats.rappels.manques_traites} appel(s) manqué(s) traité(s), en{" "}
                                        <strong>{delaiLisible(stats.rappels.delai_moyen)}</strong> en moyenne après la sonnerie.
                                    </p>
                                    {stats.rappels.par_employe.length > 0 &&
                                    <Table size="sm" className="mb-0">
                                        <thead><tr><th>Traités par</th><th className="text-right">Appels</th></tr></thead>
                                        <tbody>
                                        {stats.rappels.par_employe.map(e => (
                                            <tr key={e.id ?? "inconnu"}><td>{e.nom}</td><td className="text-right">{e.nombre}</td></tr>
                                        ))}
                                        </tbody>
                                    </Table>}
                                </CardBody>
                            </Card>
                        </Col>
                        <Col lg={6}>
                            <Card className="shadow mb-3">
                                <CardHeader><h5 className="mb-0">Clients qui appellent le plus</h5></CardHeader>
                                <CardBody>
                                    {stats.clients.length === 0
                                        ? <p className="text-muted mb-0">Aucun client reconnu sur la période.</p>
                                        : <Table size="sm" className="mb-0">
                                            <thead><tr><th>Client</th><th className="text-right">Appels</th><th className="text-right">Manqués</th></tr></thead>
                                            <tbody>
                                            {stats.clients.map(c => (
                                                <tr key={c.id}>
                                                    <td><Link to={getRoutes("Modifier Client", c.id)}>{c.nom}</Link></td>
                                                    <td className="text-right">{c.appels}</td>
                                                    <td className="text-right">{c.manques || ""}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </>}
            </Container>
        );
    }
})));

export default StatistiquesAppels;
